import type { GroupCell, KPIText, Tier } from '../../api/groupSteering'

/**
 * Presentation helpers of the group console. No target, no threshold, no
 * label lives here — they all come from the registry and the snapshot; this
 * file only knows how to print a unit and which token a tier maps to.
 */

export type Lang = 'en' | 'fr'

export function langOf(locale: string | undefined): Lang {
    return (locale ?? '').toLowerCase().startsWith('fr') ? 'fr' : 'en'
}

/** A bilingual registry string, EN as the fallback the registry guarantees. */
export function text(t: KPIText | undefined, lang: Lang): string {
    if (!t) return ''
    return t[lang] || t.en || ''
}

/** One value in its unit, for a tile, a cell, an axis. */
export function formatUnit(value: number | null | undefined, unit: string, lang: Lang, locale: string): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '—'
    const nf = (digits: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value)
    switch (unit) {
        case 'ratio':
            return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value * 100)} %`
        case 'minutes':
            return `${nf(1)} min`
        case 'hours':
            return `${nf(1)} h`
        case 'days':
            return `${nf(1)} ${lang === 'fr' ? 'j' : 'd'}`
        case 'points':
            return `${value > 0 ? '+' : ''}${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)}`
        case 'score':
            return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)}/100`
        case 'per_100_units':
            return nf(1)
        case 'count':
            return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)
        default:
            return nf(1)
    }
}

/** A byte count in the reader's locale: kB, then the largest unit that keeps it above 1, up to TB. */
export function formatBytes(bytes: number, locale: string): string {
    const units = ['kilobyte', 'megabyte', 'gigabyte', 'terabyte']
    let value = bytes / 1000
    let i = 0
    while (value >= 1000 && i < units.length - 1) {
        value /= 1000
        i++
    }
    return new Intl.NumberFormat(locale, { style: 'unit', unit: units[i], unitDisplay: 'short', maximumFractionDigits: i === 0 ? 0 : 1 }).format(value)
}

/** A delta in the unit, signed, or "=" when nothing moved. */
export function formatDelta(delta: number | null | undefined, unit: string, lang: Lang, locale: string): string {
    if (delta === null || delta === undefined) return ''
    if (Math.abs(delta) < 1e-9) return '='
    const sign = delta > 0 ? '+' : '−'
    return `${sign}${formatUnit(Math.abs(delta), unit, lang, locale)}`
}

/**
 * The tier's token classes: --chart-2 for excellent, the accent for healthy,
 * --destructive for watch, muted for none. Tokens only — no hex.
 */
export const TIER_TEXT: Record<Tier, string> = {
    excellent: 'text-[var(--chart-2)]',
    healthy: 'text-foreground',
    watch: 'text-destructive',
    none: 'text-muted-foreground',
}

export const TIER_BG: Record<Tier, string> = {
    excellent: 'bg-[var(--chart-2)]/10 border-[var(--chart-2)]/30',
    healthy: 'bg-muted/40 border-border',
    watch: 'bg-destructive/10 border-destructive/30',
    none: 'bg-muted/20 border-border',
}

/** The CSS variable a tier paints with, for charts and the map. */
export const TIER_VAR: Record<Tier, string> = {
    excellent: '--chart-2',
    healthy: '--chart-1',
    watch: '--destructive',
    none: '--muted-foreground',
}

export function tierOf(cell: GroupCell | undefined): Tier {
    return (cell?.tier as Tier | undefined) ?? 'none'
}

/**
 * Resolve a CSS token against the document, for the canvas charts that
 * cannot read `var(--x)` themselves. Same bridge as EngagementScore.tsx.
 */
export function token(name: string, fallback: string): string {
    if (typeof document === 'undefined') return fallback
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    return v || fallback
}

/** A gauge's fill ratio for a ratio/score cell, clamped to [0, 1]. */
export function gaugeRatio(value: number | null | undefined, unit: string): number | null {
    if (value === null || value === undefined) return null
    if (unit === 'ratio') return Math.max(0, Math.min(1, value))
    if (unit === 'score') return Math.max(0, Math.min(1, value / 100))
    return null
}

/** The NPS composition of a snapshot, from its breakdown rows. */
export type NpsCounts = { promoters: number; passives: number; detractors: number; total: number }

export function npsCounts(breakdown: { key: string; count: number }[] | undefined): NpsCounts {
    const get = (key: string) => breakdown?.find((b) => b.key === key)?.count ?? 0
    const promoters = get('promoters')
    const passives = get('passives')
    const detractors = get('detractors')
    return { promoters, passives, detractors, total: promoters + passives + detractors }
}

/** The NPS's ±uncertainty in points at 95 %, `round(196 / √n)`; null without answers. */
export function npsUncertainty(n: number): number | null {
    return n > 0 ? Math.round(196 / Math.sqrt(n)) : null
}
